// ============================================================================
// GESTION SOCIALE — option payante (fiche d'identification du salarié)
// ============================================================================
// Routes `/api/social/*` : titulaire seulement, et seulement si l'équipe
// PharmaGestion a activé l'option pour l'officine active (sinon 403).
// ============================================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { FicheSalarie } from '@/pages/titulaire/social/ficheSalarie';

export type StatutFiche = 'DRAFT' | 'SENT';

export interface FicheResume {
  id: string;
  label: string;
  status: StatutFiche;
  sentAt: string | null;
  sentToEmail: string | null;
  createdAt?: string;
  updatedAt: string;
}

export interface FicheComplete extends FicheResume {
  data: FicheSalarie;
}

export function useFichesSociales() {
  return useQuery({
    queryKey: ['social', 'forms'],
    queryFn: () => api.get<{ forms: FicheResume[]; defaultEmail: string | null }>('/api/social/forms'),
  });
}

export function useFicheSociale(id: string | undefined) {
  return useQuery({
    queryKey: ['social', 'forms', id],
    queryFn: () => api.get<{ form: FicheComplete }>(`/api/social/forms/${id}`).then((r) => r.form),
    enabled: !!id,
    // La fiche est éditée localement : on ne veut pas qu'un rechargement en
    // arrière-plan écrase ce que le titulaire est en train de taper.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useCreerFiche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ form: FicheComplete }>('/api/social/forms').then((r) => r.form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'forms'], exact: true }),
  });
}

export function useEnregistrerFiche(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: FicheSalarie) =>
      api.put<{ form: FicheResume }>(`/api/social/forms/${id}`, { data }).then((r) => r.form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'forms'], exact: true }),
  });
}

export function useSupprimerFiche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/social/forms/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'forms'], exact: true }),
  });
}

/** PDF régénéré à la demande — il n'est stocké nulle part (n° de sécu). */
export function usePdfFiche() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ fileName: string; pdfBase64: string }>(`/api/social/forms/${id}/pdf`).then((r) => {
        const bytes = Uint8Array.from(atob(r.pdfBase64), (c) => c.charCodeAt(0));
        return { fileName: r.fileName, blob: new Blob([bytes], { type: 'application/pdf' }) };
      }),
  });
}

export function useEnvoyerFiche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { id: string; to: string }) =>
      api.post<{ form: FicheResume }>(`/api/social/forms/${b.id}/send`, { to: b.to }).then((r) => r.form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social', 'forms'], exact: true }),
  });
}
